import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, ShieldAlert, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";

/**
 * DemoDataPurgeCard — Danger-zone action for admins to wipe every demo /
 * seed row (properties, users, tickets, vendors, etc.) before shipping the
 * app to real customers. Two-step confirm — must type "DELETE DEMO DATA"
 * to enable the commit button.
 */
export default function DemoDataPurgeCard() {
  const [scan, setScan] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const runScan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-data/scan", { dry_run: true });
      setScan(data);
      if (data.total === 0) {
        toast.success("Already clean — no demo data found.");
      } else {
        toast.success(`Found ${data.total} demo row${data.total === 1 ? "" : "s"} across ${Object.keys(data.by_collection || {}).length} collections`);
      }
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  const openPurge = async () => {
    if (!scan) await runScan();
    setConfirmText("");
    setDialogOpen(true);
  };

  const commitPurge = async () => {
    if (confirmText !== "DELETE DEMO DATA") return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-data/purge", { dry_run: false });
      toast.success(`Purged ${data.total || 0} demo row${data.total === 1 ? "" : "s"} — your database is clean.`);
      setDialogOpen(false);
      setScan(null);
      setConfirmText("");
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  return (
    <Card className="border-rose-500/30 bg-rose-500/[0.02] p-5" data-testid="demo-purge-card">
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center justify-center">
          <Trash2 className="h-5 w-5 text-rose-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-semibold text-sm">Delete demo data</div>
            <Badge variant="outline" className="border-rose-500/40 text-rose-300 text-[10px]">Danger zone</Badge>
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">
            Wipe every demo / seed row (properties, users, tickets, vendors, packages, board meetings, &hellip;) so
            you can go live with a clean slate. Your own admin account is always kept. Scoped to known demo
            property IDs &mdash; your real property data is never touched.
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Button size="sm" variant="outline" onClick={runScan} disabled={busy}
                className="border-white/10 gap-2" data-testid="demo-purge-scan">
          <Sparkles className="h-3.5 w-3.5" /> {busy ? "Scanning…" : "Scan for demo data"}
        </Button>
        {scan && scan.total > 0 && (
          <Button size="sm" onClick={openPurge} disabled={busy}
                  className="bg-rose-500 hover:bg-rose-600 gap-2" data-testid="demo-purge-open">
            <Trash2 className="h-3.5 w-3.5" /> Delete {scan.total} row{scan.total === 1 ? "" : "s"}
          </Button>
        )}
      </div>

      {scan && Object.keys(scan.by_collection || {}).length > 0 && (
        <div className="mt-3 rounded-md border border-white/10 bg-black/20 p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            Preview · {scan.total} row(s) across {Object.keys(scan.by_collection).length} collection(s)
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-[11px]">
            {Object.entries(scan.by_collection)
              .sort((a, b) => b[1] - a[1])
              .map(([col, n]) => (
                <div key={col} className="flex justify-between text-zinc-400">
                  <span className="font-mono truncate">{col}</span>
                  <span className="text-rose-300 font-semibold">{n}</span>
                </div>
              ))}
          </div>
          <div className="mt-2 text-[10px] text-zinc-600">
            Properties: {(scan.property_ids_scanned || []).join(", ")}
          </div>
        </div>
      )}

      {scan && scan.total === 0 && (
        <div className="text-[11px] text-emerald-300/80 mt-2">
          Already clean — nothing to delete.
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-rose-500/40 max-w-lg" data-testid="demo-purge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-300">
              <ShieldAlert className="h-5 w-5" />
              Delete {scan?.total || 0} demo row(s)?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-rose-500/30 bg-rose-500/[0.06] p-3">
              <div className="flex items-start gap-2 text-[12px] text-rose-200">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  This is <b>irreversible</b>. All demo properties, users, tickets, vendors, financial records,
                  and board/governance rows will be permanently deleted. Your own admin account and any real
                  property data you&#39;ve created remain untouched.
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">
                Type <span className="font-mono text-rose-300">DELETE DEMO DATA</span> to confirm
              </Label>
              <Input data-testid="demo-purge-confirm-input"
                     value={confirmText}
                     onChange={(e) => setConfirmText(e.target.value)}
                     placeholder="DELETE DEMO DATA"
                     className="mt-1 bg-white/[0.02] border-white/10 font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-rose-500 hover:bg-rose-600 disabled:opacity-40 disabled:cursor-not-allowed"
                    onClick={commitPurge}
                    disabled={busy || confirmText !== "DELETE DEMO DATA"}
                    data-testid="demo-purge-commit">
              {busy ? "Deleting…" : `Delete ${scan?.total || 0} row(s) forever`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
